import { readCommunity } from "@/features/community/records";

import type {
  WikiPageListPublic,
  WikiPagePublic,
} from "@softmax/observatory-client/types";
import { curatedText } from "@softmax/web-features/coworlds";
import { type PreviewRulesSection } from "../content/preview-details.types";
import {
  pickRulesPage,
  leadProseOf,
  isSelfDeclaredDeprecated,
  looksLikeDeveloperDoc,
  proseUnderHeading,
  rulesHeadingPattern,
} from "./preview-rules";

export async function wikiPageCount({
  signal,
  coworldName,
}: {
  signal?: AbortSignal | undefined;
  coworldName: string;
}): Promise<number | null> {
  const list = await readCommunity<WikiPageListPublic>({
    signal,
    path: `/v2/wikis/${encodeURIComponent(coworldName)}/pages`,
  });
  return list.status === "ok" ? list.data.entries.length : null;
}

async function readWikiPageBody({
  signal,
  wikiName,
  slug,
}: {
  signal?: AbortSignal | undefined;
  wikiName: string;
  slug: string;
}): Promise<string | null> {
  const result = await readCommunity<WikiPagePublic>({
    signal,
    path: `/v2/wikis/${encodeURIComponent(wikiName)}/pages/${encodeURIComponent(slug)}`,
  });
  return result.status === "ok" ? result.data.current_revision.body : null;
}

export async function readRulesContent({
  signal,
  wikiName,
}: {
  signal?: AbortSignal | undefined;
  wikiName: string;
}): Promise<PreviewRulesSection | null> {
  const list = await readCommunity<WikiPageListPublic>({
    signal,
    path: `/v2/wikis/${encodeURIComponent(wikiName)}/pages`,
  });
  const picked = pickRulesPage(list.status === "ok" ? list.data.entries : []);

  if (picked) {
    const body = await readWikiPageBody({
      signal,
      wikiName,
      slug: picked.slug,
    });
    const lead = body ? leadProseOf(body) : "";

    if (
      lead &&
      !isSelfDeclaredDeprecated(lead) &&
      !looksLikeDeveloperDoc(lead)
    ) {
      return {
        title: picked.title,
        body: curatedText(lead),
      };
    }
  }

  const mainBody = await readWikiPageBody({
    signal,
    wikiName,
    slug: "main",
  });
  const section = mainBody
    ? proseUnderHeading({
        body: mainBody,
        match: rulesHeadingPattern,
      })
    : null;
  return section &&
    !isSelfDeclaredDeprecated(section.body) &&
    !looksLikeDeveloperDoc(section.body)
    ? {
        title: section.heading,
        body: curatedText(section.body),
      }
    : null;
}
